import { MENTAL_PATTERN_LABELS } from "./mentalPatterns.js";

/** Minimum reflections before hub insights are shown. */
export const MIN_INSIGHTS_FOR_PATTERNS = 7;

export const INSUFFICIENT_INSIGHTS_MESSAGE =
  "Need data to generate the insight. Keep reflecting and come back.";

/** Short card blurbs — scannable, not dense paragraphs. */
export const HUB_CARD_DESCRIPTIONS = {
  wellness: "Pattern intensity from your thought records (0–100).",
  underStress: "How your mind protects you when pressure rises.",
  anger: "Deliberate vs reactive anger in your reflections.",
  gad7: "Anxiety pattern intensity from confirmed reflections (past 14 days)."
};

/** Under-stress behaviors shown on the hub card, keyed by dominant pattern. */
export const PATTERN_UNDER_STRESS_BEHAVIORS = {
  overthinking: [
    "Replay conversations",
    "Predict negative outcomes",
    "Seek control through analysis"
  ],
  shutting_down: [
    "Go quiet or freeze up",
    "Avoid decisions",
    "Feel mentally blank"
  ],
  people_pleasing: [
    "Say yes when you mean no",
    "Monitor others' reactions",
    "Shrink your own needs"
  ],
  staying_busy: [
    "Stay in motion to avoid feeling",
    "Pack your schedule",
    "Postpone rest"
  ],
  pulling_away: [
    "Withdraw from people",
    "Delay replies",
    "Protect by creating distance"
  ],
  irritability: [
    "Snap over small things",
    "Feel on edge",
    "Release pressure through friction"
  ],
  numbness: [
    "Feel flat or disconnected",
    "Struggle to name feelings",
    "Move through the day on autopilot"
  ],
  hyper_independence: [
    "Handle everything alone",
    "Resist asking for help",
    "Equate needing support with weakness"
  ]
};

export const DEFAULT_UNDER_STRESS_BEHAVIORS = PATTERN_UNDER_STRESS_BEHAVIORS.overthinking;

/** Trust-building factors for the mental load index. */
export const SCORE_IMPACT_FACTORS = [
  "Emotional language in reflections",
  "Stress and anger patterns",
  "Evidence validation rate",
  "Action follow-through",
  "Reflection frequency"
];

/** Signature mental loops — matched against reflection text when possible. */
export const MENTAL_LOOPS = [
  {
    id: "over_explaining",
    title: "Over-explaining yourself",
    description: "Adding extra context to feel safe or understood.",
    signals: [
      /\b(over[- ]?explain|justify myself|too much detail|rambl|defend myself|prove (?:i'm|that))\b/i
    ]
  },
  {
    id: "predicting_rejection",
    title: "Predicting rejection",
    description: "Assuming distance or disapproval before it happens.",
    signals: [
      /\b(they (?:will|won't|don't) like|going to reject|abandon|leave me|not want me|before they)\b/i,
      /\b(predict(?:ing)? (?:the worst|failure|rejection))\b/i
    ]
  },
  {
    id: "responsibility_overload",
    title: "Carrying responsibility overload",
    description: "Taking on more than your share of outcomes or emotions.",
    signals: [
      /\b(my fault|all on me|carry (?:it|everything)|responsible for (?:everyone|everything)|can't let (?:them|anyone) down)\b/i
    ]
  },
  {
    id: "certainty_before_action",
    title: "Seeking certainty before action",
    description: "Waiting for full clarity instead of testing one small step.",
    signals: [
      /\b(need to know (?:for sure|first)|can't act until|waiting until|must be certain|what if before)\b/i
    ]
  }
];

/** Pattern insight summary copy keyed by dominant stress pattern. */
export const PATTERN_SUMMARY_COPY = {
  overthinking: {
    body:
      "Your reflections show signs of cognitive overload driven by overthinking and emotional pressure. You appear to spend significant mental energy interpreting situations before validating evidence.",
    focus: [
      "Reduce interpretation loops",
      "Build evidence-based responses",
      "Prioritize reducing rumination frequency"
    ]
  },
  shutting_down: {
    body:
      "Your reflections suggest mental load may reduce input when pressure rises—shutting down to protect capacity rather than pushing through.",
    focus: [
      "One decision at a time",
      "Smaller commitments before larger ones",
      "Lower stimulation before problem-solving"
    ]
  },
  people_pleasing: {
    body:
      "Your stories often show connection maintenance under pressure — adjusting yourself to keep harmony before checking what you actually need.",
    focus: [
      "Pause before automatic yes",
      "Name one honest need per day",
      "Separate care from approval"
    ]
  },
  staying_busy: {
    body:
      "Activity and momentum show up as protection in your reflections — staying busy may be how you postpone discomfort or unprocessed feeling.",
    focus: [
      "Schedule intentional pauses",
      "Notice what you avoid when busy",
      "One reflection without fixing"
    ]
  },
  pulling_away: {
    body:
      "Distance and withdrawal appear in your reflections as a way to reduce hurt — pulling away before situations feel fully safe again.",
    focus: [
      "Low-pressure reconnection",
      "Name what you're protecting",
      "One small reach-out when ready"
    ]
  },
  irritability: {
    body:
      "Pressure seems to surface as edge and irritability in your reflections—a signal to pause before responding, not add more interpretation.",
    focus: [
      "Delay the response briefly",
      "Reduce stimulation briefly",
      "Name the interpretation under the irritation"
    ]
  },
  numbness: {
    body:
      "Flatness or disconnection in your reflections may mean intensity was dialed down to stay functional — numbness as overload protection.",
    focus: [
      "Brief thought check-ins",
      "Record one observable fact",
      "Rest without adding new interpretations"
    ]
  },
  hyper_independence: {
    body:
      "Your reflections often emphasize handling things alone — self-reliance may be protecting you from disappointment or burdening others.",
    focus: [
      "Practice small asks for help",
      "Notice cost of doing it all",
      "Receive support without explaining"
    ]
  }
};

export const DEFAULT_PATTERN_SUMMARY = PATTERN_SUMMARY_COPY.overthinking;

export function getPatternSummaryCopy(patternKey) {
  return PATTERN_SUMMARY_COPY[patternKey] || DEFAULT_PATTERN_SUMMARY;
}

export function getUnderStressBehaviors(patternKey) {
  return PATTERN_UNDER_STRESS_BEHAVIORS[patternKey] || DEFAULT_UNDER_STRESS_BEHAVIORS;
}

export function formatStressTrendLabel(patternKey) {
  const name = MENTAL_PATTERN_LABELS[patternKey] || "Stress pattern";
  return `↑ ${name} increased`;
}
